import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { post } from "../api/setting/actions";
import { useSofiaV2Launch } from "./sofia-v2-launch-context";

const NURO_V2_PATH = "/Sofia-v2";
const CLICK_AI_V2_PATH = "/salesAgent-settingsApi/click_ai_v2/";

/* Reveal duration of the strip -> full-screen launch takeover */
const REVEAL_MS = 1100;
/* How long the welcome screen plays on its own before the iframe even starts
   loading. The site only begins loading after this, so its first render
   (confetti) is seen from the start once the overlay lifts. */
const WELCOME_MS = 2000;
/* Fade-out duration once the iframe is ready behind the overlay */
const FADE_MS = 350;

const launchCss = `
  .nv2-strip-halo {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 70%;
    height: 340%;
    border-radius: 9999px;
    filter: blur(24px);
    opacity: 0.95;
    pointer-events: none;
    transform: translate(-50%, -50%);
    will-change: transform, opacity;
  }
  .nv2-strip-halo-a {
    background: radial-gradient(circle, hsl(265 95% 38%), transparent 70%);
    animation: nv2-strip-halo-a 11s ease-in-out infinite;
  }
  .nv2-strip-halo-b {
    background: radial-gradient(circle, hsl(220 95% 35%), transparent 70%);
    animation: nv2-strip-halo-b 13s ease-in-out infinite;
    animation-delay: -3s;
  }
  .nv2-strip-halo-c {
    background: radial-gradient(circle, hsl(295 90% 42%), transparent 70%);
    animation: nv2-strip-halo-c 9s ease-in-out infinite;
    animation-delay: -5s;
    opacity: 0.85;
  }
  @keyframes nv2-strip-halo-a {
    0%, 100% { transform: translate(-60%, -50%) scale(1); }
    50%      { transform: translate(-40%, -50%) scale(1.15); }
  }
  @keyframes nv2-strip-halo-b {
    0%, 100% { transform: translate(-40%, -50%) scale(1.1); }
    50%      { transform: translate(-60%, -50%) scale(1); }
  }
  @keyframes nv2-strip-halo-c {
    0%, 100% { transform: translate(-50%, -55%) scale(0.95); }
    50%      { transform: translate(-50%, -45%) scale(1.15); }
  }

  /* ── Launch takeover reveal ── */
  @keyframes nv2-launch-reveal {
    from { clip-path: inset(0 0 calc(100% - 44px) 0); }
    to   { clip-path: inset(0 0 0% 0); }
  }

  /* Invisible timer: fires animationend after the minimum read time. */
  .nv2-launch-hold {
    position: absolute;
    width: 0; height: 0;
    opacity: 0;
    pointer-events: none;
    animation-name: nv2-launch-hold;
    animation-timing-function: linear;
    animation-fill-mode: both;
  }
  @keyframes nv2-launch-hold {
    0%   { opacity: 0; }
    100% { opacity: 0; }
  }

  .nv2-launch-burst {
    position: absolute; inset: 0;
    background: radial-gradient(circle at 50% 45%,
      rgba(255,255,255,0.18) 0%,
      rgba(180,160,255,0.10) 22%,
      transparent 60%);
    animation: nv2-launch-burst-pulse 4.5s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes nv2-launch-burst-pulse {
    0%,100% { opacity: 0.85; transform: scale(1); }
    50%     { opacity: 1;    transform: scale(1.06); }
  }

  .nv2-ai-halo {
    position: absolute;
    border-radius: 9999px;
    filter: blur(80px);
    pointer-events: none;
    will-change: transform, opacity;
    opacity: 0.95;
  }
  .nv2-ai-halo-purple {
    width: 70vmin; height: 70vmin;
    left: 15%; top: 20%;
    background: radial-gradient(circle, hsl(265 95% 38%) 0%, transparent 70%);
    animation: nv2-ai-halo-wave-a 14s ease-in-out infinite;
  }
  .nv2-ai-halo-blue {
    width: 75vmin; height: 75vmin;
    right: 10%; top: 28%;
    background: radial-gradient(circle, hsl(220 95% 35%) 0%, transparent 70%);
    animation: nv2-ai-halo-wave-b 16s ease-in-out infinite;
  }
  .nv2-ai-halo-pink {
    width: 60vmin; height: 60vmin;
    left: 32%; bottom: 8%;
    background: radial-gradient(circle, hsl(295 90% 42%) 0%, transparent 70%);
    animation: nv2-ai-halo-wave-c 13s ease-in-out infinite;
    opacity: 0.85;
  }
  .nv2-ai-halo-cyan {
    width: 55vmin; height: 55vmin;
    left: 4%; bottom: 16%;
    background: radial-gradient(circle, hsl(240 90% 38%) 0%, transparent 70%);
    animation: nv2-ai-halo-wave-a 18s ease-in-out infinite reverse;
    opacity: 0.8;
  }
  .nv2-ai-halo-violet {
    width: 65vmin; height: 65vmin;
    right: 6%; bottom: 6%;
    background: radial-gradient(circle, hsl(280 95% 40%) 0%, transparent 70%);
    animation: nv2-ai-halo-wave-b 15s ease-in-out infinite reverse;
  }
  @keyframes nv2-ai-halo-wave-a {
    0%,100% { transform: translate(0,0) scale(1); }
    25%     { transform: translate(6vw,-4vh) scale(1.15); }
    50%     { transform: translate(-4vw,5vh) scale(1.25); }
    75%     { transform: translate(5vw,3vh) scale(1.1); }
  }
  @keyframes nv2-ai-halo-wave-b {
    0%,100% { transform: translate(0,0) scale(1); }
    25%     { transform: translate(-7vw,4vh) scale(1.2); }
    50%     { transform: translate(5vw,-5vh) scale(1.3); }
    75%     { transform: translate(-4vw,-3vh) scale(1.12); }
  }
  @keyframes nv2-ai-halo-wave-c {
    0%,100% { transform: translate(0,0) scale(1); }
    33%     { transform: translate(5vw,5vh) scale(1.22); }
    66%     { transform: translate(-6vw,-4vh) scale(1.3); }
  }

  .nv2-launch-rays {
    position: absolute; left: 50%; top: 45%;
    width: 180vmax; height: 180vmax;
    transform: translate(-50%,-50%);
    background:
      repeating-conic-gradient(from 0deg at 50% 50%,
        rgba(255,240,210,0.10) 0deg 4deg,
        transparent 4deg 14deg);
    mix-blend-mode: overlay;
    opacity: 0.55;
    animation: nv2-launch-rays-spin 38s linear infinite;
    pointer-events: none;
  }
  @keyframes nv2-launch-rays-spin { to { transform: translate(-50%,-50%) rotate(360deg); } }

  .nv2-launch-ring {
    position: absolute; left: 50%; top: 45%;
    border-radius: 9999px;
    border: 1px solid rgba(255,255,255,0.22);
    transform: translate(-50%,-50%);
    pointer-events: none;
    box-shadow: inset 0 0 60px rgba(255,255,255,0.08);
  }
  .nv2-launch-ring-1 { width: 38vmin; height: 38vmin; animation: nv2-launch-ring-pop 1s cubic-bezier(0.16,1,0.3,1) 0.2s both, nv2-launch-ring-float 9s ease-in-out infinite 1s; }
  .nv2-launch-ring-2 { width: 62vmin; height: 62vmin; border-color: rgba(255,255,255,0.16); animation: nv2-launch-ring-pop 1.1s cubic-bezier(0.16,1,0.3,1) 0.35s both, nv2-launch-ring-float 11s ease-in-out infinite 1.2s reverse; }
  .nv2-launch-ring-3 { width: 92vmin; height: 92vmin; border-color: rgba(255,255,255,0.10); animation: nv2-launch-ring-pop 1.2s cubic-bezier(0.16,1,0.3,1) 0.5s both, nv2-launch-ring-float 13s ease-in-out infinite 1.4s; }
  @keyframes nv2-launch-ring-pop {
    from { opacity: 0; transform: translate(-50%,-50%) scale(0.6); }
    to   { opacity: 1; transform: translate(-50%,-50%) scale(1); }
  }
  @keyframes nv2-launch-ring-float {
    0%,100% { transform: translate(-50%,-50%) scale(1); }
    50%     { transform: translate(-50%,-50%) scale(1.04); }
  }

  .nv2-launch-spark-dot {
    position: absolute;
    width: 4px; height: 4px;
    border-radius: 9999px;
    background: rgba(255,240,210,0.95);
    box-shadow: 0 0 12px rgba(255,220,170,0.9);
    opacity: 0;
    animation: nv2-launch-spark-fly ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes nv2-launch-spark-fly {
    0%   { opacity: 0; transform: translateY(8px) scale(0.6); }
    50%  { opacity: 1; transform: translateY(-6px) scale(1.1); }
    100% { opacity: 0; transform: translateY(-18px) scale(0.6); }
  }

  @keyframes nv2-launch-chip-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.96); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes nv2-launch-line-up {
    from { opacity: 0; transform: translateY(22px); filter: blur(6px); }
    to   { opacity: 1; transform: translateY(0); filter: blur(0); }
  }
  @keyframes nv2-launch-headline-glow {
    from { opacity: 0; transform: translateY(28px) scale(0.96); filter: blur(8px); }
    to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
  }
  @keyframes nv2-dot-pulse {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.35; }
  }
  .nv2-launch-v2-shine {
    position: absolute; inset: 0;
    background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.85) 50%, transparent 70%);
    background-size: 220% 100%;
    background-clip: text; -webkit-background-clip: text;
    color: transparent;
    mix-blend-mode: overlay;
    animation: nv2-launch-v2-shine-sweep 2.8s ease-in-out infinite 1.2s;
    pointer-events: none;
  }
  @keyframes nv2-launch-v2-shine-sweep {
    0%   { background-position: 200% 0; opacity: 0; }
    30%  { opacity: 1; }
    100% { background-position: -120% 0; opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .nv2-strip-halo,
    .nv2-launch-burst, .nv2-launch-rays, .nv2-launch-ring,
    .nv2-launch-spark-dot, .nv2-launch-v2-shine, .nv2-ai-halo {
      animation: none !important;
    }
  }
`;

const FEATURE_PILLS = ["Faster Replies", "Smarter Follow-ups", "Live Voice", "Deeper CRM"];

export default function NuroV2Banner() {
  const navigate = useNavigate();
  const launch = useSofiaV2Launch();

  /* overlay mounted */
  const [expanding, setExpanding] = useState(false);
  /* fade-out in progress (iframe is ready behind the overlay) */
  const [closing, setClosing] = useState(false);

  const iframeReady = launch?.iframeReady ?? false;

  const handleClick = useCallback(() => {
    if (expanding) return;
    post({}, CLICK_AI_V2_PATH).catch((error) => console.error("click_ai_v2:", error));
    setClosing(false);
    launch?.reset();
    setExpanding(true);
    /* Navigate now so the page mounts, but it won't start loading the site
       until we call beginLoad() after the welcome screen has played. */
    navigate(NURO_V2_PATH, { state: { fromStrip: true } });
  }, [expanding, navigate, launch]);

  /* Once the iframe has finished loading (it only starts after the welcome
     screen), fade the overlay out to reveal the freshly rendered site. */
  useEffect(() => {
    if (expanding && iframeReady && !closing) {
      setClosing(true);
    }
  }, [expanding, iframeReady, closing]);

  const handleOverlayAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>) => {
      if (e.animationName === "nv2-launch-hold") {
        /* Welcome screen has played long enough — now start loading the site. */
        launch?.beginLoad();
      }
    },
    [launch]
  );

  const handleOverlayTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.propertyName === "opacity" && closing) {
        setExpanding(false);
        setClosing(false);
        launch?.reset();
      }
    },
    [closing, launch]
  );

  return (
    <>
      <style>{launchCss}</style>

      <Box
        component="button"
        type="button"
        onClick={handleClick}
        aria-label="The new Sofia is here. V2 is coming soon. Reserve your spot."
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: 44,
          flexShrink: 0,
          p: 0,
          border: "none",
          overflow: "hidden",
          cursor: "pointer",
          background: "hsl(24 100% 50%)",
          "&:hover .nv2-strip-halo": { opacity: 1 },
          "&:hover .nv2-cta": {
            textDecoration: "underline",
            textUnderlineOffset: "4px",
          },
        }}
      >
        <Box component="span" aria-hidden className="nv2-strip-halo nv2-strip-halo-a" />
        <Box component="span" aria-hidden className="nv2-strip-halo nv2-strip-halo-b" />
        <Box component="span" aria-hidden className="nv2-strip-halo nv2-strip-halo-c" />

        <Box
          sx={{
            position: "relative",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            px: 2,
            gap: 1,
            minWidth: 0,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 14, color: "hsl(45 100% 85%)", flexShrink: 0 }} />
          <Typography
            component="span"
            sx={{
              color: "#fff",
              fontSize: { xs: "12px", sm: "14px" },
              fontWeight: 500,
              lineHeight: 1.4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            The new Sofia is here - V2 is coming soon.{" "}
            <Box
              component="span"
              className="nv2-cta"
              sx={{ ml: 1, color: "hsl(45 100% 90%)", fontWeight: 700, textDecoration: "none" }}
            >
              Reserve your spot →
            </Box>
          </Typography>
        </Box>

        <Box
          component="span"
          aria-hidden
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            zIndex: 10,
            background:
              "linear-gradient(90deg, transparent, hsl(25 100% 60% / 0.8), hsl(15 90% 55% / 0.8), transparent)",
          }}
        />
      </Box>

      {expanding && (
        <Box
          aria-hidden
          onAnimationEnd={handleOverlayAnimationEnd}
          onTransitionEnd={handleOverlayTransitionEnd}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1300,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 3,
            color: "#fff",
            background: "hsl(263 70% 18%)",
            opacity: closing ? 0 : 1,
            transition: `opacity ${FADE_MS}ms ease`,
            animation: `nv2-launch-reveal ${REVEAL_MS}ms cubic-bezier(0.16,1,0.3,1) both`,
          }}
        >
          <Box
            component="span"
            aria-hidden
            className="nv2-launch-hold"
            sx={{ animationDuration: `${WELCOME_MS}ms` }}
          />

          <Box component="span" aria-hidden className="nv2-ai-halo nv2-ai-halo-purple" />
          <Box component="span" aria-hidden className="nv2-ai-halo nv2-ai-halo-blue" />
          <Box component="span" aria-hidden className="nv2-ai-halo nv2-ai-halo-pink" />
          <Box component="span" aria-hidden className="nv2-ai-halo nv2-ai-halo-cyan" />
          <Box component="span" aria-hidden className="nv2-ai-halo nv2-ai-halo-violet" />

          <Box component="span" aria-hidden className="nv2-launch-burst" />
          <Box component="span" aria-hidden className="nv2-launch-rays" />

          <Box component="span" aria-hidden className="nv2-launch-ring nv2-launch-ring-1" />
          <Box component="span" aria-hidden className="nv2-launch-ring nv2-launch-ring-2" />
          <Box component="span" aria-hidden className="nv2-launch-ring nv2-launch-ring-3" />

          {[...Array(18)].map((_, i) => (
            <Box
              key={i}
              component="span"
              aria-hidden
              className="nv2-launch-spark-dot"
              sx={{
                left: `${(i * 53) % 100}%`,
                top: `${(i * 37) % 100}%`,
                animationDelay: `${(i * 0.18) % 2.4}s`,
                animationDuration: `${2.2 + (i % 5) * 0.4}s`,
              }}
            />
          ))}

          <Box
            sx={{
              position: "relative",
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 2.5, sm: 3.5 },
              maxWidth: "64rem",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: "9999px",
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                background: "rgba(255,255,255,0.14)",
                border: "1px solid rgba(255,255,255,0.35)",
                color: "hsl(45 100% 92%)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.25)",
                animation: "nv2-launch-chip-in 0.7s cubic-bezier(0.16,1,0.3,1) 0.15s both",
              }}
            >
              <AutoAwesomeIcon sx={{ width: 14, height: 14 }} />
              Now Launching
              <Box
                component="span"
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: "9999px",
                  bgcolor: "rgba(255,255,255,0.6)",
                }}
              />
              Drivee Sofia V2
            </Box>

            <Typography
              component="h2"
              sx={{
                color: "#fff",
                fontWeight: 900,
                lineHeight: 0.95,
                letterSpacing: "-0.03em",
                fontSize: { xs: "2.75rem", sm: "3.75rem", lg: "6rem", xl: "8.5rem" },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "block",
                  textShadow: "0 4px 24px rgba(0,0,0,0.28)",
                  animation: "nv2-launch-line-up 0.9s cubic-bezier(0.16,1,0.3,1) 0.25s both",
                }}
              >
                Welcome to
              </Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  textShadow: "0 4px 24px rgba(0,0,0,0.28)",
                  animation: "nv2-launch-line-up 0.9s cubic-bezier(0.16,1,0.3,1) 0.45s both",
                }}
              >
                the next era of
              </Box>
              <Box
                component="span"
                sx={{
                  display: "block",
                  backgroundImage:
                    "linear-gradient(180deg, #fff 0%, hsl(45 100% 92%) 50%, hsl(40 100% 78%) 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 8px 30px rgba(0,0,0,0.35))",
                  animation: "nv2-launch-headline-glow 0.9s cubic-bezier(0.16,1,0.3,1) 0.7s both",
                }}
              >
                Sofia
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    ml: { xs: 1.5, sm: 2.5 },
                    position: "relative",
                    verticalAlign: "baseline",
                    fontFamily: "Orbitron, Inter, sans-serif",
                    letterSpacing: "-0.04em",
                  }}
                >
                  V2.
                  <Box component="span" aria-hidden className="nv2-launch-v2-shine" />
                </Box>
              </Box>
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1, sm: 1.5 },
                mt: 0.5,
                animation: "nv2-launch-line-up 0.9s cubic-bezier(0.16,1,0.3,1) 1s both",
              }}
            >
              {FEATURE_PILLS.map((label) => (
                <Box
                  key={label}
                  component="span"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "9999px",
                    fontSize: { xs: "11px", sm: "12px" },
                    fontWeight: 600,
                    color: "rgba(255,255,255,0.95)",
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  {label}
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mt: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                color: "rgba(255,255,255,0.8)",
                fontSize: { xs: "12px", sm: "14px" },
                animation: "nv2-launch-line-up 0.9s cubic-bezier(0.16,1,0.3,1) 1.2s both",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "9999px",
                  bgcolor: "#fff",
                  animation: "nv2-dot-pulse 1.6s ease-in-out infinite",
                }}
              />
              Taking you in…
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}
